import { useCallback, useState } from 'react';
import { STORAGE_KEYS } from '../config/config';

/**
 * Resolve the initial desktop primary-rail collapse preference from
 * localStorage. Mirrors ThemeModeContext's read-on-init pattern and is SSR /
 * no-`window` safe. Defaults to the full (expanded) rail.
 */
function getInitialSidebarCollapsed(): boolean {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem(STORAGE_KEYS.SIDEBAR_COLLAPSED) === 'true';
}

export interface SidebarCollapsedState {
  /** True when the desktop primary rail should render icon-only (collapsed). */
  collapsed: boolean;
  /** Flip the collapse state; the choice is persisted to localStorage. */
  toggle: () => void;
}

/**
 * Owns the desktop-only "collapse the primary nav rail" preference. The value is
 * restored on init and written back on every toggle (same read-on-init +
 * write-on-change contract as ThemeModeContext). Mobile ignores this entirely —
 * the consumer only applies it at the desktop breakpoint.
 */
export function useSidebarCollapsed(): SidebarCollapsedState {
  const [collapsed, setCollapsed] = useState<boolean>(getInitialSidebarCollapsed);

  const toggle = useCallback(() => {
    setCollapsed((current) => {
      const next = !current;
      localStorage.setItem(STORAGE_KEYS.SIDEBAR_COLLAPSED, String(next));
      return next;
    });
  }, []);

  return { collapsed, toggle };
}
